/**
 * web-studio-sites D7 — the required-part check run after every site drawing:
 * each part the piece's current state requires is present in the placeholder,
 * inside its list entry (the element whose `data-item` names it), and calls its
 * action where it has one. An authoring diagnostic, not a proof of honesty.
 */
import type { RequiredPart } from '../contract/required';

function present(root: HTMLElement, required: RequiredPart): boolean {
  return [...root.querySelectorAll<HTMLElement>(`[data-part="${required.part}"]`)].some((el) => {
    if (required.item !== undefined) {
      const holder = required.in ? el.closest<HTMLElement>(`[data-part="${required.in}"]`) : el;
      if (!holder || !root.contains(holder) || holder.dataset.item !== required.item) return false;
    }
    if (required.action !== undefined) {
      const caller = el.closest<HTMLElement>('[data-action]');
      if (!caller || !root.contains(caller) || caller.dataset.action !== required.action) return false;
    }
    return true;
  });
}

/** The first required part the drawing left out, or null. */
export function missingPart(root: HTMLElement, required: readonly RequiredPart[]): RequiredPart | null {
  return required.find((r) => !present(root, r)) ?? null;
}

/** The console line naming the piece, the item and the part. */
export function missingPartMessage(piece: string, missing: RequiredPart): string {
  const where = missing.item !== undefined ? `item "${missing.item}" has` : 'the drawing has';
  const what = missing.action ? `"${missing.part}" part that calls ${missing.action}` : `"${missing.part}" part`;
  return `[telify] ${piece}: ${where} no ${what}; showing the standard drawing.`;
}
